import * as React from 'react';
import * as csx from 'csstips';
import * as gls from '../../ap/layout/gls';
import * as typestyle from 'typestyle';
import { colors, maxPageContentWidth, maxFormContentWidth, spacing } from '../../ap/base/styles';
import { Anchor, HeaderAnchor } from '../../ap/base/anchor';
import { ProjectName } from './projectName';
import { route, linkTo, docs, navigateTo, redirectToRegistration } from '../../links';
import { Footer, FooterStyles } from '../../ap/base/footer';
import { APLogo } from '../../ap/base/icons';
import { SecondaryButton, PrimaryButton } from '../../ap/base/button';
import { trackLinkClick } from '../../ap/omniture/omnitureService';
import { config } from '../../config/config';
import {authenticationService} from "../../webServices/authenticationService";


export namespace PageStyles {
  const headerHeight = 80;

  export const fadeIn = typestyle.keyframes({
    from: {
      opacity: 0
    },
    to: {
      opacity: 1
    }
  });

  export const rootClassName = typestyle.style(
    csx.flex,
    csx.vertical,
    csx.scroll,
    {
      height: '100%',
      width: '100%',
    }
  );

  export const headerClassName = typestyle.style(
    csx.content,
    {
      backgroundColor: colors.white,
      height: '80px',
      $nest: {
        '@media screen and (max-width: 768px)': {
          height: 'auto',
        }
      }
    }
  );

  export const headerLinkClassName = typestyle.style(
    {
      display: 'flex',
      flexFlow: 'row wrap',
      justifyContent: 'space-around',
      width: 'auto'
    }
  );

  export const headerCenteredContentClassName = typestyle.style(
    csx.flex,
    csx.horizontal,
    csx.center,
    csx.padding(15),
    {
      maxWidth: `${maxPageContentWidth}px`,
      margin: '0 auto',
      backgroundColor: colors.white,
    }
  );

  export const headerLogoContainerClassName = typestyle.style(
    csx.flexRoot,
    csx.horizontal,
    csx.center,
    {
      textDecoration: 'none',

      $nest: {
        '&:focus': {
          color: colors.red,
          outline: 'thin dotted',
          outlineColor: colors.red,
        }
      },
    }
  );

  export const bodyContainerClass = typestyle.style(
    csx.content,
    csx.padding(spacing.betweenPageSections),
    {
      minHeight: `calc(100% - ${headerHeight + FooterStyles.footerHeight}px)`,
      animationName: fadeIn,
      animationDuration: '.5s',
    }
  );
}

interface PageProps {
  /**
   * The sections will be contained in a vertically padded layout
   */
  sections: React.ReactNode
  loggedIn: boolean,
  seamless?: boolean,
  isLogin?: boolean
}

/**
 * For Layout with
 * - Header up top
 * - body sections that take
 * - Footer on bottom
 * This is the layout for that
 */
export const Page = (props: PageProps) => {
  const omnitureOnClickHeaderLogout = () => trackLinkClick('navigation','header:logout');
  const omnitureOnClickHeaderLogin = () => trackLinkClick('navigation','header:login');
  const omnitureOnClickHeaderSignUp = () => trackLinkClick('navigation','header:sign up');

  const omnitureOnClickFooterTnC = () => trackLinkClick('navigation','footer:t&c');
  const omnitureOnClickFooterPrivatePolicy = () => trackLinkClick('navigation', 'footer:privacy policy');
  const omnitureOnClickFooterHelp = () => trackLinkClick('navigation', 'footer:help');
  const omnitureOnClickFooterReportGuide = () => trackLinkClick('navigation', 'footer:report guide');

  const logoutLinkClick = (event): void => {
    event.preventDefault();
    omnitureOnClickHeaderLogout();

    authenticationService.logout().then(() => {
      sessionStorage.clear();
    });
  }

  return (
    <div data-comment="HeaderBodyCenteredPage" className={PageStyles.rootClassName}>
      <div data-comment="HeaderBodyCenteredPage Header" className={PageStyles.headerClassName}>
        <div className={PageStyles.headerCenteredContentClassName}>
          <a className={PageStyles.headerLogoContainerClassName} href={linkTo(route.dash())}>
            <div style={{ display: 'inline-block' }} >
              <APLogo />
            </div>
            <gls.SmallHorizontalSpace space={10} />
            <ProjectName />
          </a>

          <gls.Flex />

          {
            !!props.loggedIn ?
            (
              <div>
                <Anchor id="logoutLink" href="" onClick={logoutLinkClick}><span>Logout</span></Anchor>
              </div>
            ) : (
              <div className={PageStyles.headerLinkClassName} >
                <HeaderAnchor id="loginLink" href={config.portalConfig.portalLoginUrl} onClick={omnitureOnClickHeaderLogin}>{!props.isLogin && "Log in"}</HeaderAnchor>
                <Anchor id="signuplink" href={redirectToRegistration()} target="_blank" onClick={omnitureOnClickHeaderSignUp}><PrimaryButton id="signup" text="Sign up"/></Anchor>

              </div>
            )

          }
        </div>
      </div>
      <div data-comment="HeaderBodyPage BodyContainer" className={PageStyles.bodyContainerClass} style={props.seamless ? { padding: '0px' } : {}}>
        <gls.ContentVerticalContentMargined margin={props.seamless ? 0 : spacing.betweenPageSections} children={props.sections} />
      </div>
      <Footer links={[
        { href: docs.termsAndConditions(), text: 'Terms & Conditions',  subSection: omnitureOnClickFooterTnC},
        { href: docs.privacyPolicy(), text: 'Privacy Policy', subSection: omnitureOnClickFooterPrivatePolicy},
        { href: docs.help(), text: 'Help', subSection: omnitureOnClickFooterHelp},
        { href: docs.reportGuide(), text: 'Report Guide', subSection: omnitureOnClickFooterReportGuide }
      ]} />
    </div>
  );
};
